/**
 * useScrollReveal
 *
 * Returns a ref and a boolean `isVisible` that becomes true once the element
 * enters the viewport. The observer triggers only once — the element stays
 * visible after it has been seen.
 *
 * Respects `prefers-reduced-motion`: when the user has opted out of motion,
 * `isVisible` starts as `true` so no animation is played at all.
 */

import { useRef, useState, useEffect } from 'react';

export interface ScrollRevealOptions {
  /** Fraction of the element that must be visible before triggering (0–1). Default 0.15. */
  threshold?: number;
  /** Margin around the root viewport. Default "0px 0px -40px 0px". */
  rootMargin?: string;
}

export interface ScrollRevealResult {
  ref: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
}

export function useScrollReveal(options: ScrollRevealOptions = {}): ScrollRevealResult {
  const { threshold = 0.15, rootMargin = '0px 0px -40px 0px' } = options;

  // Immediately visible when reduced motion is preferred — no animation.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Trigger once, then stop observing.
          observer.unobserve(node);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [prefersReducedMotion, threshold, rootMargin]);

  return { ref, isVisible };
}
